import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import { useLocation, useNavigate } from 'react-router-dom';
import { TabCountBadge } from '../tabCountBadge/tabCountBadge.component';

export interface TabNavItem {
  key: string;
  label: string;
  path: string;
  /** Item count shown as a badge next to the label. */
  count?: number;
  /**
   * Extra path prefixes that also belong to this tab, for pages that do not
   * live under `path` (e.g. Permission groups sitting next to Users).
   */
  alsoMatches?: string[];
}

interface TabNavProps {
  tabs: TabNavItem[];
  /** Render as the compact pill bar used for tabs nested inside a section. */
  sub?: boolean;
}

const isUnder = (pathname: string, base: string) =>
  pathname === base || pathname.startsWith(`${base}/`);

/**
 * Route-driven tab bar. The selected tab is whichever one owns the longest
 * prefix of the current path, so a nested route ("/tenant/settings/numbering")
 * selects the nested tab rather than its parent ("/tenant/settings"), and a
 * detail route keeps its list's tab selected without any per-route bookkeeping.
 */
export const TabNav = ({ tabs, sub = false }: TabNavProps) => {
  const { pathname } = useLocation();
  const navigate = useNavigate();

  // A bar with a single tab tells the viewer nothing they cannot already see.
  if (tabs.length < 2) {
    return null;
  }

  const matchLength = (tab: TabNavItem) =>
    [tab.path, ...(tab.alsoMatches ?? [])].reduce(
      (longest, base) => (isUnder(pathname, base) ? Math.max(longest, base.length) : longest),
      -1
    );

  const selected = tabs.reduce(
    (best, tab, index) => {
      const length = matchLength(tab);
      return length > best.length ? { index, length } : best;
    },
    { index: 0, length: -1 }
  ).index;

  return (
    <Tabs
      className={sub ? 'subTabs' : undefined}
      value={selected}
      variant="scrollable"
      scrollButtons="auto"
    >
      {tabs.map((tab) => (
        <Tab
          key={tab.key}
          label={<TabCountBadge label={tab.label} count={tab.count} inline={sub} />}
          onClick={() => navigate(tab.path)}
        />
      ))}
    </Tabs>
  );
};
